import * as angular from 'angular';
import { config, reactShell } from 'angular/utils/config';

type LeadTimeRuleRuntimeLength = {
    value: number;
    timeUnit: LeadTimeRuntimeTimeUnit;
};

type LeadTimeRuntimeTimeUnit =
    | 'HOURS'
    | 'DAYS'
    | 'MONTHS'
    | 'QUARTERS'
    | 'YEARS';

export const LeadTimeRuntimeTimeUnitLabel: Record<
    LeadTimeRuntimeTimeUnit,
    string
> = {
    HOURS: 'hours',
    DAYS: 'days',
    MONTHS: 'months',
    QUARTERS: 'quarters',
    YEARS: 'years'
};

type LeadTimeTimeUnit =
    | 'YEAR'
    | 'MONTH'
    | 'WORKING_DAY'
    | 'DAY'
    | 'HOUR'
    | 'MINUTE';

export const LeadTimeTimeUnitLabel: Record<LeadTimeTimeUnit, string> = {
    MINUTE: 'minutes',
    HOUR: 'hours',
    DAY: 'days',
    WORKING_DAY: 'working days',
    MONTH: 'months',
    YEAR: 'years'
};

export type LeadTimeDto =
    | {
          value: number;
          leadTimeUnit: LeadTimeTimeUnit;
      }
    | {
          value?: null;
          leadTimeUnit: 'AT_ANY_TIME';
      };

export type LeadTimeRule = {
    name: string;
    contractPeriodType:
        | 'NON_WITHINDAY'
        | 'FULL_WITHINDAY'
        | 'SUBSEQUENT_WITHINDAY';
    leadTimeStart: LeadTimeDto;
    leadTimeEnd: LeadTimeDto;
} & (
    | {
          infiniteRuntime: true;
          runtimeLength?: null;
      }
    | {
          infiniteRuntime: false;
          runtimeLength: LeadTimeRuleRuntimeLength;
      }
);

export type LeadTimeSettings = {
    leadTimeRulesEnabled: boolean;
    leadTimeRules: LeadTimeRule[];
};

export class RegularFcfsService {
    async networkPointLeadTimeSettings(networkPointId: string) {
        if (!config.regularFcfs) return;
        try {
            const response = await reactShell.axios.request<LeadTimeSettings>({
                url: `${config.regularFcfs}/api/network-points/${networkPointId}/lead-time-settings`
            });
            if (response.data.leadTimeRules)
                response.data.leadTimeRules.sort(compareLeadTimeRules);

            return response.data;
        } catch (e) {
            if (
                e &&
                e.name === 'AxiosError' &&
                e.response &&
                (e.response.status === 401 || e.response.status === 403)
            )
                return;
            console.error(e);
        }
    }
}

const periodTypeRank: Record<LeadTimeRule['contractPeriodType'], number> = {
    SUBSEQUENT_WITHINDAY: 1,
    FULL_WITHINDAY: 2,
    NON_WITHINDAY: 3
};

const runtimeLengthTimeUnitInHours: Record<LeadTimeRuntimeTimeUnit, number> = {
    HOURS: 1,
    DAYS: 24,
    MONTHS: 24 * 30,
    QUARTERS: 24 * 30 * 3,
    YEARS: 24 * 30 * 12
};

function compareLeadTimeRules(a: LeadTimeRule, b: LeadTimeRule) {
    let cmp =
        periodTypeRank[a.contractPeriodType] -
        periodTypeRank[b.contractPeriodType];
    if (cmp === 0) {
        const lengthA = a.infiniteRuntime
            ? Number.MAX_SAFE_INTEGER
            : a.runtimeLength.value *
              runtimeLengthTimeUnitInHours[a.runtimeLength.timeUnit];
        const lengthB = b.infiniteRuntime
            ? Number.MAX_SAFE_INTEGER
            : b.runtimeLength.value *
              runtimeLengthTimeUnitInHours[b.runtimeLength.timeUnit];
        cmp = lengthA - lengthB;
    }
    return cmp;
}

angular.module('app').service('regularFcfsService', RegularFcfsService);
